import { triggerAdminLogout, getAdminAccessToken } from "../../../context/AdminAuthContext";
import { devLog, devWarn } from "../../../utils/devLogger";
import { callApi, HttpMethod } from "../../methods/callApi";

export async function callAdminApi<T = any>({
  title,
  url,
  method = 'POST',
  body,
  params,
  isCallPageLoader = false,
  withToken = true, // 기본값을 true로 변경 (대부분의 관리자 API는 인증이 필요)
}: {
  title: string;
  url: string;
  method?: HttpMethod;
  body?: Record<string, any>;
  params?: Record<string, any>;
  isCallPageLoader?: boolean;
  withToken?: boolean;
  onOpenLoader?: () => void;
  onCloseLoader?: () => void;
}): Promise<{ data: T; headers?: Headers }> {
  devLog("API 요청 (토큰 인증):", title);

  // withToken이 true일 때만 토큰을 가져옴
  let accessToken = withToken ? getAdminAccessToken() : undefined;

  // 언어 설정 헤더 추가
  const savedLang = localStorage.getItem('appLang');
  const currentLang = savedLang || (navigator.language.startsWith('ko') ? 'ko' : 'en');
  const customHeaders: Record<string, string> = {
    'X-Language': currentLang
  };
  devLog("언어 헤더 설정:", currentLang);

  // withToken이 true인데 토큰이 없으면 인증 실패
  if (withToken && !accessToken) {
    devWarn('관리자 토큰이 없음 - CMS 로그인으로 리다이렉트');
    triggerAdminLogout();
    window.location.href = '/cms/login';
    throw new Error("No admin token - redirecting to login");
  }

  devLog('API 호출 정보:', {
    title,
    url,
    withToken,
    토큰상태: accessToken ? '토큰 전송' : '토큰 없음',
    adminId: sessionStorage.getItem('adminId') ? '존재함' : '없음'
  }); 

  const result = await callApi({
    title,
    url,
    method,
    params,
    accessToken: accessToken || undefined,
    headers: customHeaders,
    body,
    isCallPageLoader,
  });

  // 401 에러 또는 인증 관련 에러 감지 (로그인 API가 아닌 경우에만)
  if (withToken && (result.data?.statusCode === 401 || 
      (result.data?.message && (
        result.data.message.includes("token is invalid") ||
        result.data.message.includes("unauthorized") ||
        result.data.message.includes("Unauthorized") ||
        result.data.message.includes("토큰") ||
        result.data.message.includes("인증")
      )))) {
    devWarn('토큰이 유효하지 않음 - CMS 로그인으로 리다이렉트');
    devWarn('에러 응답:', result.data);
    
    // 로그아웃 처리 및 CMS 로그인 페이지로 리다이렉트
    triggerAdminLogout();
    window.location.href = '/cms/login';
    throw new Error("Token invalid - redirecting to login");
  }

  return result;
}

// 편의 함수들
export const callAdminApiGet = <T = any>(params: Omit<Parameters<typeof callAdminApi>[0], 'method'>) => 
  callAdminApi<T>({ ...params, method: 'GET' });

export const callAdminApiPost = <T = any>(params: Omit<Parameters<typeof callAdminApi>[0], 'method'>) => 
  callAdminApi<T>({ ...params, method: 'POST' });

export const callAdminApiPut = <T = any>(params: Omit<Parameters<typeof callAdminApi>[0], 'method'>) => 
  callAdminApi<T>({ ...params, method: 'PUT' });

export const callAdminApiDelete = <T = any>(params: Omit<Parameters<typeof callAdminApi>[0], 'method'>) => 
  callAdminApi<T>({ ...params, method: 'DELETE' });
